import { GoogleGenerativeAI } from '@google/generative-ai';
import type { AIProvider, AIMessage, RecommendationRequest, RecommendationResponse } from './aiProvider';
import type { ProductRecommendation } from '@/types/product';

export class GeminiProvider implements AIProvider {
  private genAI: GoogleGenerativeAI;
  private model: any;

  constructor(apiKey: string) {
    this.genAI = new GoogleGenerativeAI(apiKey);
    this.model = this.genAI.getGenerativeModel({ model: 'gemini-2.5-flash' });
  }

  private async retryWithBackoff<T>(fn: () => Promise<T>, maxRetries = 3): Promise<T> {
    for (let i = 0; i < maxRetries; i++) {
      try {
        return await fn();
      } catch (error: any) {
        const is503 = error?.message?.includes('503') || error?.message?.includes('high demand');
        if (i === maxRetries - 1 || !is503) {
          throw error;
        }
        // Exponential backoff: 1s, 2s, 4s
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, i) * 1000));
      }
    }
    throw new Error('Max retries reached');
  }

  async generateRecommendations(request: RecommendationRequest): Promise<RecommendationResponse> {
    const systemPrompt = `You are a knowledgeable outdoor gear specialist at PeakPath, a premium adventure equipment retailer.

Your task: Analyze the customer's request and recommend the most relevant products from the catalogue.

Context:
${request.userHistory ? `
Previous searches: ${request.userHistory.prompts.join(', ')}
Previously viewed categories: ${request.userHistory.categories.join(', ')}
` : ''}

Instructions:
1. Understand the customer's activity, conditions, preferences (size, colour, gender, budget)
2. Match products based on:
   - Activity suitability
   - Technical requirements (waterproof, insulation, terrain type)
   - Size/fit preferences
   - Colour preferences
   - Budget signals
3. Check stock availability - prefer in-stock items
4. Return a ranked list (max 12 products) with relevance scores (0-1)
5. Provide a brief, specific reason for each recommendation

Product catalogue:
${JSON.stringify(request.products.map(p => ({
  id: p.id,
  name: p.name,
  brand: p.brand,
  category: p.category,
  subcategory: p.subcategory,
  price: p.price,
  salePrice: p.salePrice,
  description: p.description,
  features: p.features,
  colours: p.colours,
  sizes: p.sizes,
  tags: p.tags,
  rating: p.rating,
  hasStock: Object.values(p.stock).some(qty => qty > 0),
})), null, 2)}

Customer request: "${request.prompt}"

Respond with ONLY valid JSON in this exact format:
{
  "summary": "Brief 1-sentence understanding of the request",
  "recommendations": [
    {
      "productId": "product-id",
      "relevanceScore": 0.95,
      "reason": "Specific reason why this product fits their needs"
    }
  ]
}`;

    try {
      const result = await this.retryWithBackoff<any>(() =>
        this.model.generateContent(systemPrompt)
      );
      const response = result.response.text();

      // Extract JSON from response (handle markdown code blocks)
      let jsonText = response.trim();
      if (jsonText.startsWith('```json')) {
        jsonText = jsonText.replace(/```json\n?/g, '').replace(/```\n?$/g, '');
      } else if (jsonText.startsWith('```')) {
        jsonText = jsonText.replace(/```\n?/g, '').replace(/```\n?$/g, '');
      }

      const parsed = JSON.parse(jsonText);

      // Map product IDs to full product objects
      const recommendations: ProductRecommendation[] = parsed.recommendations
        .map((rec: any) => {
          const product = request.products.find(p => p.id === rec.productId);
          if (!product) return null;

          return {
            product,
            relevanceScore: rec.relevanceScore,
            reason: rec.reason,
          };
        })
        .filter(Boolean)
        .slice(0, 12); // Max 12 recommendations

      return {
        recommendations,
        summary: parsed.summary || 'Found matching products',
      };
    } catch (error) {
      console.error('Gemini recommendation error:', error);
      throw new Error('Failed to generate recommendations');
    }
  }

  async generateResponse(messages: AIMessage[]): Promise<string> {
    const formatted = messages.map(m => ({
      role: m.role === 'assistant' ? 'model' : 'user',
      parts: [{ text: m.content }],
    }));

    try {
      const chat = this.model.startChat({
        history: formatted.slice(0, -1),
      });

      const result = await this.retryWithBackoff<any>(() =>
        chat.sendMessage(formatted[formatted.length - 1].parts[0].text)
      );
      return result.response.text();
    } catch (error) {
      console.error('Gemini chat error:', error);
      throw new Error('Failed to generate response');
    }
  }

  async extractSearchIntent(prompt: string) {
    const systemPrompt = `Extract search intent from this query. Return ONLY valid JSON.

Query: "${prompt}"

Return format:
{
  "categories": ["category1"],
  "brands": ["brand1"],
  "priceRange": [min, max] or null,
  "colours": ["colour1"],
  "sizes": ["size1"],
  "tags": ["tag1"],
  "activity": "activity type"
}`;

    try {
      const result = await this.retryWithBackoff<any>(() =>
        this.model.generateContent(systemPrompt)
      );
      const response = result.response.text();

      let jsonText = response.trim();
      if (jsonText.startsWith('```json')) {
        jsonText = jsonText.replace(/```json\n?/g, '').replace(/```\n?$/g, '');
      } else if (jsonText.startsWith('```')) {
        jsonText = jsonText.replace(/```\n?/g, '').replace(/```\n?$/g, '');
      }

      return JSON.parse(jsonText);
    } catch (error) {
      console.error('Intent extraction error:', error);
      return {};
    }
  }
}
